import '../../utils/window'

// Tracks analytics events using event-name attribute and v-event-props directive
export function initializeEventNameTracking(app: any): void {
    const TARGET_SELECTOR = 'button, a'

    const logEvent = typeof window !== 'undefined' && typeof window.logEvent === 'function' ? window.logEvent : null

    if (!logEvent) {
        window.logger?.error('[EventNameTracking] logEvent is not available at plugin install time.')
        return
    }
    const isTargetElement = (el: HTMLElement) => el.closest(TARGET_SELECTOR) !== null

    function createDirective(property: string) {
        function mounted(el: HTMLElement, binding: any) {
            if (!isTargetElement(el)) return
            ;(el as any)[property] = binding.value
        }

        function updated(el: HTMLElement, binding: any) {
            if (!isTargetElement(el)) return
            ;(el as any)[property] = binding.value
        }

        function unmounted(el: HTMLElement) {
            delete (el as any)[property]
        }

        return {
            v2: { bind: mounted, update: updated, unbind: unmounted },
            v3: { mounted, updated, unmounted },
        }
    }

    const eventPropsDirective = createDirective('__eventProps')
    const eventNameDirective = createDirective('__eventNameValue')

    // Register directives based on Vue version
    const vueVersion = parseInt(app.version.split('.')[0], 10)
    if (vueVersion >= 3) {
        app.directive('event-props', eventPropsDirective.v3)
        app.directive('event-name', eventNameDirective.v3)
    } else {
        app.directive('event-props', eventPropsDirective.v2)
        app.directive('event-name', eventNameDirective.v2)
    }

    const handleClick = (event: Event) => {
        const target = event.target
        if (!target || !(target instanceof Element)) return

        const clickable = target.closest(TARGET_SELECTOR)
        if (!clickable) return

        let eventName = clickable.getAttribute('event-name')?.trim()

        // If no event-name attribute, try to get it from the v-event-name directive
        if (!eventName) {
            const eventNameValue = (clickable as any).__eventNameValue
            if (eventNameValue !== undefined && eventNameValue !== null) {
                try {
                    if (typeof eventNameValue === 'function') {
                        // It's a function - call it with event and element
                        eventName = eventNameValue(event, clickable)
                    } else {
                        // It's a string/reactive value - use it directly
                        eventName = String(eventNameValue).trim()
                    }
                } catch (err) {
                    window.logger?.error('[EventNameTracking] v-event-name processing threw:', { error: err })
                    return
                }
            }
        }

        if (!eventName) return

        try {
            const eventProps = (clickable as any).__eventProps
            if (clickable.hasAttribute('event-beacon') && typeof window.logEventBeacon === 'function') {
                window.logEventBeacon(eventName, eventProps)
            } else {
                logEvent(eventName, eventProps)
            }
        } catch (err) {
            window.logger?.error('[EventNameTracking] logEvent threw:', { error: err })
        }
    }

    document.addEventListener('click', handleClick, true)
}
